import { Link } from 'react-router-dom';
import { RecordsTable, type Column, type FilterDef } from '../components/RecordsTable/RecordsTable';
import { zipToState } from '../lib/zipState';
import './Leads.css';

/**
 * The rep CRM pipeline, in the admin console. Backlog 1082, first slice.
 *
 * ============================================================================
 * WHY THIS EXISTS HERE AND NOT ON A PORTAL OF ITS OWN
 * ============================================================================
 * Mike ruled 2026-09-10: **"Reps are outsourced staff ... Not partners. Their stuff goes in
 * admin."** That sharpened a 2026-09-04 ruling which had already said reps sign in to this
 * console rather than a partner-facing surface — the newer wording moves the DESTINATION, not
 * just the door.
 *
 * `cabana-reps` (reps.cabanafilters.com) predates both rulings by a week. It is still live and
 * still serving; nothing here breaks it. The order Mike chose: **build the rep surface in
 * admin first, retire the portal after.** So this is additive, and the portal keeps working
 * until somebody decides its screens are all covered.
 *
 * THIS IS THE FIRST SLICE, NOT THE WHOLE SURFACE. The portal also has appointments,
 * commissions, referrals, a dashboard and a settings screen. Leads went first because it is
 * the CRM core the others orbit — an appointment and a commission both hang off a lead — and
 * because every MCP tool it needs already exists.
 *
 * NO NEW BACKEND. Every read and write here is an existing `cabana_rep_leads` MCP tool, the
 * same ones the portal calls. Nothing was added to the API or the database for this.
 */

interface LeadRow {
  id: number;
  public_id: string;
  company_name: string;
  contact_first_name: string | null;
  contact_last_name: string | null;
  email: string | null;
  phone: string | null;
  primary_zip: string | null;
  territory: string | null;
  stage: string;
  interest_level: string | null;
  source: string | null;
  assigned_rep_id: number | null;
  next_followup_at: string | null;
  last_activity_at: string | null;
  status: string;
  created_at: string;
}

/** The seven CHECK-constrained stages, in pipeline order — see migration 013. */
const STAGES = ['new', 'contacted', 'qualified', 'demo_scheduled', 'proposal', 'won', 'lost'] as const;

export function stageLabel(s: string): string {
  return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Late, today, soon, or nothing — the only distinction a follow-up date needs at a glance. */
export function followupTone(iso: string | null): 'overdue' | 'today' | 'future' | 'none' {
  if (!iso) return 'none';
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return 'none';
  const now = new Date();
  const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999).getTime();
  if (t < now.getTime()) return 'overdue';
  if (t <= endOfToday) return 'today';
  return 'future';
}

function shortDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const COLUMNS: Column<LeadRow>[] = [
  {
    key: 'company_name',
    header: 'Company',
    render: (r) => (
      <Link to={`/leads/${r.id}`} className="leads__link">
        {r.company_name}
      </Link>
    ),
  },
  {
    key: 'contact',
    header: 'Contact',
    render: (r) => [r.contact_first_name, r.contact_last_name].filter(Boolean).join(' ') || '—',
  },
  {
    key: 'stage',
    header: 'Stage',
    render: (r) => <span className={`leads__stage leads__stage--${r.stage}`}>{stageLabel(r.stage)}</span>,
  },
  {
    key: 'interest_level',
    header: 'Interest',
    render: (r) =>
      r.interest_level ? (
        <span className={`leads__interest leads__interest--${r.interest_level}`}>{r.interest_level}</span>
      ) : (
        '—'
      ),
  },
  {
    key: 'territory',
    header: 'Territory',
    // The portal shows territory; a ZIP is more use when territory is unset, which it often is.
    render: (r) => r.territory || zipToState(r.primary_zip) || r.primary_zip || '—',
  },
  {
    key: 'next_followup_at',
    header: 'Follow-up',
    render: (r) => {
      const tone = followupTone(r.next_followup_at);
      return <span className={`leads__followup leads__followup--${tone}`}>{shortDate(r.next_followup_at)}</span>;
    },
  },
  { key: 'last_activity_at', header: 'Last activity', render: (r) => shortDate(r.last_activity_at) },
];

const FILTERS: FilterDef<LeadRow>[] = [
  {
    key: 'stage',
    label: 'Stage',
    options: [{ value: 'all', label: 'All' }, ...STAGES.map((s) => ({ value: s, label: stageLabel(s) }))],
    match: (r, v) => r.stage === v,
  },
  {
    key: 'interest_level',
    label: 'Interest',
    options: [
      { value: 'all', label: 'All' },
      { value: 'hot', label: 'Hot' },
      { value: 'warm', label: 'Warm' },
      { value: 'cold', label: 'Cold' },
    ],
    match: (r, v) => r.interest_level === v,
  },
  {
    key: 'assigned',
    label: 'Assignment',
    options: [
      { value: 'all', label: 'All' },
      { value: 'assigned', label: 'Assigned' },
      { value: 'unassigned', label: 'Unassigned' },
    ],
    match: (r, v) => (v === 'assigned' ? r.assigned_rep_id != null : r.assigned_rep_id == null),
  },
];

export function Leads() {
  return (
    <RecordsTable<LeadRow>
      title="Leads"
      intro="The rep pipeline — HVAC contractors being worked toward becoming Cabana dealers. Each lead carries a stage, an interest level and a follow-up date; click a company to open its full history. Reps are outsourced staff, so their pipeline lives here rather than on a portal of its own."
      tool="listLeads"
      /* status: 'active' deliberately. Deactivated leads are soft-deleted rows kept for
         history; showing them by default makes a worked pipeline look twice its real size. */
      args={{ limit: 200, status: 'active' }}
      select={(r) => (r as { leads: LeadRow[] }).leads ?? []}
      columns={COLUMNS}
      rowKey={(r) => r.id}
      emptyMessage="No leads in the pipeline yet."
      refreshable
      searchable={{
        placeholder: 'Search company, contact, email, territory or ZIP…',
        match: (r, q) =>
          [
            r.company_name,
            r.contact_first_name,
            r.contact_last_name,
            r.email,
            r.territory,
            r.primary_zip,
          ].some((f) => f != null && f.toLowerCase().includes(q)),
      }}
      filters={FILTERS}
      rowClassName={(r) =>
        followupTone(r.next_followup_at) === 'overdue' ? 'leads__row--overdue' : undefined
      }
    />
  );
}
